import grey from "@mui/material/colors/grey";
import { ThemeOptions, darken } from "@mui/material/styles";

// Shoutout to claude for the clutch

const baseTheme: ThemeOptions = {
  palette: {
    primary: {
      main: grey[600],
      dark: grey[600],
      light: grey[300],
    },
    action: {
      hover: "rgb(0,0,0,.08)",
      hoverOpacity: 0.08,
    },
    background: {
      drawer: grey[200],
      menu: grey[100],
      dark: grey[900],
      dark_transparent: "rgba(0,0,0,.6)",
    },
    progress: {
      progress: "rgb(38, 156, 136, 1)",
      remaining: "rgb(38, 156, 136, 0.6)",
      complete: "rgb(38, 156, 136, 1)",
    },
    testRow: {
      default: "#fff4e4",
      hover: darken("#fff4e4", 0.05),
      selected: "#ffeacc",
      selectedHover: darken("#ffeacc", 0.05),
    },
    notificationRow: {
      default: "#ffe9e4",
      hover: darken("#ffe9e4", 0.05),
      selected: "#ffd7cc",
      selectedHover: darken("#ffd7cc", 0.05),
    },
  },
  // transitions: {
  //   duration: {
  //     enteringScreen: 1500,
  //   },
  // },
  typography: {
    htmlFontSize: 16, // base font size used to calculate 1.0 REM
    h1: {
      fontFamily: "Roboto",
      fontStyle: "normal",
      fontSize: "2rem",
      lineHeight: 1.4,
      letterSpacing: "0.46px",
    },
    h2: {
      fontFamily: "Roboto",
      fontSize: "1.75rem",
    },
    h3: {
      fontFamily: "Roboto",
      fontSize: "1.8rem",
    },
    h4: {
      fontFamily: "Roboto",
      fontSize: "1.4rem",
    },
    subtitle1: {
      fontFamily: "Open Sans",
      fontStyle: "normal",
      fontSize: 15,
      color: "rgba(0, 0, 0, 0.38)",
    },
  },
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          minWidth: "48px",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          whiteSpace: "nowrap",
        },
        contained: {
          borderRadius: "9999px", // Makes contained buttons circular
        },
        outlined: {
          borderRadius: "9999px", // Makes outlined buttons circular
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "32px",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.MuiInputBase-root.MuiOutlinedInput-root": {
            // Target only outlined variant
            borderRadius: "3px",
            backgroundColor: "rgba(0, 0, 0, 0.03)",
            padding: "0 15px", // Remove vertical padding here
            height: "38px",
            boxSizing: "border-box",
            border: "1px solid rgba(0, 0, 0, 0.03)",
            transition: "border-color 0.1s ease-in-out",

            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },

            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },

            "&.MuiInputBase-sizeSmall": {
              padding: "0 12px",
              height: "32px",
              "& .MuiOutlinedInput-input": {
                padding: "5px 0", // Smaller padding for small variant
                height: "22px",
                lineHeight: "22px",
              },
            },

            "&.MuiInputBase-multiline": {
              height: "auto",
              borderRadius: "3px",
              padding: "8px 12px",
            },

            "& .MuiOutlinedInput-input": {
              padding: "8px 0", // Add padding to input element instead
              height: "22px", // Set explicit height for input
              lineHeight: "22px", // Match height for proper text alignment
              "&:-webkit-autofill": {
                WebkitBoxShadow: "0 0 0 30px rgba(0, 0, 0, 0.03) inset",
                WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
              },
            },

            "&.Mui-focused": {
              border: "1px solid rgba(0, 0, 0, 0.3)",
            },
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          "&.MuiInputBase-root.MuiFilledInput-root": {
            borderRadius: "3px",
            backgroundColor: "rgba(0, 0, 0, 0.06)",
            padding: "0 15px",
            height: "38px",
            boxSizing: "border-box",
            border: "none",
            borderBottom: "2px solid rgba(0, 0, 0, 0.12)",
            transition: "all 0.1s ease-in-out",

            "&:before, &:after": {
              display: "none",
            },

            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.08)",
              borderBottom: "2px solid rgba(0, 0, 0, 0.2)",
            },

            "&.MuiInputBase-sizeSmall": {
              padding: "0 12px",
              height: "32px",
              "& .MuiFilledInput-input": {
                padding: "4px 0",
                height: "20px",
                lineHeight: "20px",
              },
            },

            "&.MuiInputBase-multiline": {
              height: "auto",
              padding: "8px 12px",
              borderBottom: "none",
            },

            "& .MuiFilledInput-input": {
              padding: "6px 0",
              height: "22px",
              lineHeight: "22px",
              "&:-webkit-autofill": {
                WebkitBoxShadow: "0 0 0 30px rgba(0, 0, 0, 0.06) inset",
                WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
              },
            },

            "&.Mui-focused": {
              backgroundColor: "rgba(0, 0, 0, 0.08)",
              borderBottom: "2px solid rgba(0, 0, 0, 0.3)",
            },
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          "&.MuiInputBase-root": {
            height: "38px",
            boxSizing: "border-box",

            "&.MuiInputBase-sizeSmall": {
              height: "32px",

              "& .MuiInput-input": {
                padding: "4px 0",
                height: "24px",
                lineHeight: "24px",
              },
            },

            "& .MuiInput-input": {
              height: "38px",
              lineHeight: "38px",
              padding: "0",
            },

            "&.MuiInputBase-multiline": {
              height: "auto",
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          position: "relative",
          transform: "translate(10px, 4px) scale(0.75)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(10px, 4px) scale(0.75)",
          },
          "&:not(.MuiInputLabel-shrink)": {
            transform: "translate(10px, 4px) scale(0.75)",
          },
        },
        filled: {
          position: "relative",
          transform: "translate(10px, 4px) scale(0.75)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(10px, 4px) scale(0.75)",
          },
          "&:not(.MuiInputLabel-shrink)": {
            transform: "translate(10px, 4px) scale(0.75)",
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginTop: "0px", // Reduced from 8px to 4px for tighter spacing
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: {
          "& .MuiPaper-root": {
            borderRadius: "8px",
          },
        },
        listbox: {
          padding: "6px 0",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: "8px",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: "8px",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "8px",
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          "& .MuiPaper-root": {
            borderRadius: "8px",
          },
        },
      },
    },
  },
};

export default baseTheme;
